<template>
  <div class="outer">
    <div class="container" style="margin-bottom:80px">
      <hgroup>
        <el-button class="is-round" @click="$router.back()">
          <BaseIcon name="chevron-left" icon-style="fal" class="mr-2 text-secondary" />
          返回
        </el-button>
        <h1>房型設定</h1>
      </hgroup>

      <div class="content" v-loading="!isDataReady" element-loading-text="載入中...">
        <h3>
          當前設定分店：
          <span style="color:#EA6A29">{{ productName }} </span>
        </h3>
        <el-form v-if="isDataReady" class="step1" :rules="rules" :model="form" ref="formRef" label-position="top">
          <div class="field">
            <div class="form-block-title mt-0 mb-3">
              <span class="isStyleRequired">名稱</span>
            </div>
            <el-form-item prop="title">
              <el-input v-model="form.title" placeholder="請輸入名稱" />
            </el-form-item>
          </div>

          <div class="field">
            <div class="form-block-title mt-0 mb-3">
              <span>英文名稱</span>
            </div>
            <el-form-item prop="enTitle">
              <el-input v-model="form.enTitle" placeholder="請輸入英文名稱" />
            </el-form-item>
          </div>

          <div class="field">
            <div class="form-block-title mt-0 mb-3">
              <span>副標題</span>
            </div>
            <el-form-item prop="subTitle">
              <el-input v-model="form.subTitle" placeholder="請輸入副標題" />
            </el-form-item>
          </div>

          <div class="field">
            <div class="form-block-title mt-0 mb-3">
              <span class="isStyleRequired">房間最多可容納數</span>
            </div>
            <el-form-item prop="maxPets">
              <el-input v-model.number="form.maxPets" type="number" :min="1" :max="10" />
            </el-form-item>
          </div>

          <div class="field">
            <div class="form-block-title mt-0 mb-3">
              <span class="isStyleRequired">房間數</span>
            </div>
            <el-form-item prop="quantity">
              <el-input v-model.number="form.quantity" type="number" :min="1" :max="100" />
            </el-form-item>
          </div>

          <div class="field">
            <div class="form-block-title mt-0 mb-3">
              <span class="isStyleRequired">房間起始編號</span>
            </div>
            <el-form-item class="room-number-group" prop="code">
              <el-input v-model="form.code" placeholder="請輸入標題" />
              <span class="dash">-</span>
              <el-input v-model="form.startNumber" disabled />
            </el-form-item>
          </div>

          <div class="form-block-title mt-0 mb-3">
            <span class="isStyleRequired">房間設備</span>
          </div>
          <div class="field block">
            <el-form-item prop="features">
              <div class="tag-group" v-if="form.features && form.features.length">
                <el-tag v-for="feature in form.features" :key="feature.value" :closable="true" @close="removeFeature(feature)" class="mr-2 mb-2">
                  {{ feature.label }}
                </el-tag>
              </div>
            </el-form-item>
            <el-button class="is-round" @click="onClickOpenRoomFeatureDialog">
              <BaseIcon name="plus" />
              新增房間設備
            </el-button>
          </div>

          <div class="field textarea">
            <div class="form-block-title mt-0 mb-3">
              <span>項目描述</span>
            </div>
            <el-form-item prop="description">
              <el-input type="textarea" v-model="form.description" :rows="6" placeholder="請輸入內容" />
            </el-form-item>
          </div>

          <el-dialog class="merchant-dialog" title="選取房間設備" :visible.sync="isRoomFeatureDialogShow" width="80%" top="5vh" append-to-body>
            <el-form label-position="top" class="isWrap" @submit.native.prevent>
              <el-form-item>
                <el-row type="flex" :gutter="10">
                  <el-col :span="18">
                    <el-input v-model="featureSearchText" placeholder="搜尋" class="search-in-box" @keydown.enter.native.prevent>
                      <i slot="suffix" class="el-input__icon el-icon-search"></i>
                    </el-input>
                  </el-col>
                  <el-col :span="6">
                    <el-button round type="primary" style="width:100%" @click.prevent="addFeatureIfNotFound">新增設備</el-button>
                  </el-col>
                </el-row>
              </el-form-item>

              <div class="content">
                <el-checkbox :value="checkAllFeatureIds" :indeterminate="isIndeterminateFeature" class="check-all" @change="toggleAllFeatureIds">
                  全選
                  <div class="total">{{ allFeatures.length }}</div>
                </el-checkbox>

                <el-checkbox-group v-model="selectedFeatureIds">
                  <el-row :gutter="20">
                    <el-col v-for="f in filterFeature" :key="f.value" :span="12">
                      <el-checkbox :label="f.value" class="feature-item">
                        {{ f.label }}
                        <i class="el-icon-delete" @click.stop.prevent="onDeleteFeature(f.value)" />
                      </el-checkbox>
                    </el-col>
                  </el-row>
                </el-checkbox-group>
              </div>
            </el-form>
            <div slot="footer" class="two-row">
              <el-button class="is-round" @click="isRoomFeatureDialogShow = false">取消</el-button>
              <el-button class="is-round" type="primary" @click="saveSelectedFeatureIds">確定</el-button>
            </div>
          </el-dialog>
        </el-form>
        <div v-else style="height: 400px;"></div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { getHotelRoomTypeFeature, postHotelRoomTypeFeature, deleteHotelRoomTypeFeature } from '../api'

export default {
  props: {
    roomData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isDataReady: false,
      isRoomFeatureDialogShow: false,
      originalFormData: {},
      form: {
        title: '',
        enTitle: '',
        subTitle: '',
        maxPets: 1,
        quantity: 1,
        code: '',
        startNumber: 1,
        description: '',
        features: [], // 儲存字串陣列，對應 API 格式
      },
      featureSearchText: '',
      selectedFeatureIds: [], // 彈窗中勾選的 ID 陣列
      allFeatures: [], // { label, value } 格式
      rules: {
        title: [{ required: true, message: '名稱為必填', trigger: 'blur' }],
        maxPets: [{ required: true, message: '房間最多可容納數為必填', trigger: 'blur' }, { type: 'number', min: 1, max: 10, message: '範圍需在 1-10 之間', trigger: 'blur' }],
        quantity: [{ required: true, message: '房間數為必填', trigger: 'blur' }, { type: 'number', min: 1, max: 100, message: '範圍需在 1-100 之間', trigger: 'blur' }],
        code: [{ required: true, message: '房間起始編號為必填', trigger: 'blur' }],
        features: [{ type: 'array', required: true, message: '至少選擇一個房間設備', trigger: 'change' }],
      },
    }
  },
  watch: {
    roomData: {
      handler(val) {
        if (val && Object.keys(val).length > 0) {
          const deepCopiedData = JSON.parse(JSON.stringify(val))
          this.form = {
            ...this.form,
            ...deepCopiedData,
            features: Array.isArray(deepCopiedData.features) ? deepCopiedData.features : [],
          }
          this.originalFormData = {
            ...this.form,
            ...deepCopiedData,
            features: Array.isArray(deepCopiedData.features) ? deepCopiedData.features : [],
          }
          this.isDataReady = true
          this.$message.success('房型設定資料載入完成')
        }
      },
      immediate: true,
      deep: true,
    },
  },
  computed: {
    filterFeature() {
      return this.allFeatures.filter((f) => f.label.toLowerCase().includes(this.featureSearchText.toLowerCase()))
    },
    checkAllFeatureIds() {
      return this.selectedFeatureIds.length === this.allFeatures.length && this.allFeatures.length > 0
    },
    isIndeterminateFeature() {
      return this.selectedFeatureIds.length > 0 && this.selectedFeatureIds.length < this.allFeatures.length
    },
    ...mapGetters('hotel/setting', ['productId', 'productName']),
  },
  methods: {
    removeFeature(feature) {
      this.form.features = this.form.features.filter((e) => e !== feature)
    },
    toggleAllFeatureIds(val) {
      this.selectedFeatureIds = val ? this.allFeatures.map((f) => f.value) : []
    },
    saveSelectedFeatureIds() {
      // 從 allFeatures 找出 ID 對應的 label
      this.form.features = this.allFeatures.filter((f) => this.selectedFeatureIds.includes(f.value))
      this.isRoomFeatureDialogShow = false
    },
    async onClickOpenRoomFeatureDialog() {
      const success = await this.fetchRoomTypeFeatures()
      if (success) {
        this.selectedFeatureIds = this.form.features.map((f) => f.value)
        this.isRoomFeatureDialogShow = true
      }
    },
    async fetchRoomTypeFeatures() {
      try {
        const res = await getHotelRoomTypeFeature(this.productId)
        const data = res.items || res // 視 API 結構而定
        this.allFeatures = data.map((f) => ({
          label: f.name,
          value: f.id,
        }))
        return true
      } catch (error) {
        this.$message.error('取得設備失敗')
        return false
      }
    },
    async addFeatureIfNotFound() {
      if (!this.featureSearchText) return
      const matched = this.allFeatures.find((f) => f.label === this.featureSearchText)
      if (!matched) {
        try {
          const res = await postHotelRoomTypeFeature(this.productId, { name: this.featureSearchText })
          const newItem = { label: res.items.name, value: res.items.id }
          this.allFeatures.push(newItem)
          this.selectedFeatureIds.push(newItem.value)
          this.featureSearchText = ''
          this.$message.success('新增成功')
        } catch (e) {
          this.$message.error('新增失敗')
        }
      }
    },
    async onDeleteFeature(featureId) {
      try {
        await deleteHotelRoomTypeFeature(this.productId, featureId)
        this.$message.success('刪除成功')
        await this.fetchRoomTypeFeatures()
        // 刪除後同步移除已勾選狀態
        this.selectedFeatureIds = this.selectedFeatureIds.filter((id) => id !== featureId)
      } catch (e) {
        this.$message.error('刪除失敗')
      }
    },
    async validateForm() {
      try {
        return await this.$refs.formRef.validate()
      } catch (error) {
        this.$message.error('尚有必填資料未填寫')
        return false
      }
    },
    getStepData() {
      return {
        title: this.form.title,
        enTitle: this.form.enTitle,
        subTitle: this.form.subTitle,
        maxPets: this.form.maxPets,
        quantity: this.form.quantity,
        code: this.form.code,
        description: this.form.description,
        // 重組 features: 從 [{label, value}] 轉回 API 格式
        features: this.form.features,
      }
    },
    resetData() {
      this.form = JSON.parse(JSON.stringify(this.originalFormData))
    },
  },
}
</script>

<style lang="scss" scoped>
/* 保持您原本的樣式，並針對 checkbox 修正一處 layout */
.outer {
  overflow-y: auto;
  width: 100%;
  padding: 18px 20px 0;
  height: 100%;
  margin-bottom: 45px;
}
.container {
  width: 100%;
  hgroup {
    display: flex;
    align-items: center;
    .el-button {
      margin-right: 20px;
    }
  }
  .content {
    margin-top: 10px;
    padding: 18px 20px 20px;
    background-color: #fff;
  }
}
.two-row {
  display: flex;
  gap: 20px;
  .el-button {
    flex: 1;
  }
}
.step1 .field {
  margin-bottom: 20px;
  &.block {
    padding: 20px;
    background: #f9f9fb;
    border-radius: 8px;

    .el-tag {
      width: 100%;
      color: #6c6b6b;
      position: relative;
      background-color: white;
      border: 1px solid #f2f2f2;
      border-radius: 8px;

      ::v-deep .el-icon-close {
        position: absolute;
        top: 7px;
        right: 4px;
        color: #121931;
      }
    }

    .el-button {
      margin-top: 20px;
      width: 100%;
    }
  }
  &.textarea {
    margin-top: 24px;

    ::v-deep .el-textarea__inner {
      background-color: white;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 12px;
    }
  }
}
.room-number-group ::v-deep .el-form-item__content {
  display: flex;
  align-items: center;
  gap: 15px;
  .dash {
    flex-shrink: 0;
  }
}

.feature-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 15px 20px;
  border: 1px solid #eaeaea;
  border-radius: 8px;
  margin: 14px 0 0 0;

  ::v-deep .el-checkbox__label {
    flex: 1;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  span {
    color: #606266;
    font-size: 14px;
  }
}
.delete-icon {
  position: absolute;
  right: 12px;
  cursor: pointer;
  color: #909399;
  z-index: 10;
  &:hover {
    color: #f56c6c;
  }
}

.merchant-dialog {
  .check-all {
    width: 100%;
    background: #f9f9fb;
    padding: 14px;
    border-radius: 8px 8px 0 0;
    border: 1px solid #e5e7eb;
    position: relative;
    .total {
      position: absolute;
      right: 15px;
      top: 14px;
    }
  }
  .content {
    border: 1px solid #e5e7eb;
    border-top: none;
    border-radius: 0 0 8px 8px;
    max-height: 400px;
    overflow-y: auto;
    padding: 15px;
  }
}
</style>
